import { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronLeft, ChevronRight, Play, Pause, SkipBack, Download, Film, ImageOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

const THUMB_W = 112;
const THUMB_H = 84;
const THUMB_GAP = 8;
const PRELOAD_RADIUS = 12;

function useFrameImage(getFrameThumbnail, frame, enabled = true) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(null);
    setFailed(false);
    if (!enabled || !getFrameThumbnail) return undefined;

    let cancelled = false;
    getFrameThumbnail(frame)
      .then((dataUrl) => {
        if (!cancelled) setSrc(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [getFrameThumbnail, frame, enabled]);

  return { src, failed };
}

function FrameSlide({
  frame,
  getFrameThumbnail,
  isActive,
  isSelected,
  shouldLoad,
  onClick,
  onToggleSelect,
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const { src, failed } = useFrameImage(getFrameThumbnail, frame, shouldLoad || visible);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setVisible(true);
      },
      { rootMargin: "160px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      onToggleSelect(frame, e.shiftKey);
    } else {
      onClick(frame);
    }
  };

  return (
    <button
      ref={ref}
      type="button"
      data-testid={`frame-thumb-${frame}`}
      onClick={handleClick}
      className={cn(
        "group relative flex-shrink-0 rounded-md overflow-hidden transition-all duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950",
        "border bg-zinc-900/80",
        isActive
          ? "border-primary ring-2 ring-primary/50 scale-[1.04] z-10 shadow-lg shadow-primary/20"
          : "border-zinc-700/80 hover:border-zinc-500",
        isSelected && !isActive && "border-emerald-500/70 ring-1 ring-emerald-500/40"
      )}
      style={{ width: THUMB_W, height: THUMB_H }}
      aria-label={`Frame ${frame + 1}${isSelected ? ", selected" : ""}`}
      aria-pressed={isSelected}
      aria-current={isActive ? "true" : undefined}
    >
      <div className="absolute inset-0 bg-black flex items-center justify-center">
        {src ? (
          <img
            src={src}
            alt={`Frame ${frame + 1} preview`}
            className="w-full h-full object-contain"
            draggable={false}
          />
        ) : failed ? (
          <ImageOff className="w-5 h-5 text-zinc-600" />
        ) : (
          <div className="w-full h-full bg-zinc-800/80 animate-pulse" />
        )}
      </div>

      {isSelected && (
        <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-emerald-500 text-[10px] font-bold text-white flex items-center justify-center shadow-sm">
          ✓
        </span>
      )}

      <span
        className={cn(
          "absolute bottom-0 inset-x-0 py-0.5 text-[10px] font-medium text-center tabular-nums",
          isActive ? "bg-primary/90 text-white" : "bg-black/70 text-zinc-200 group-hover:bg-black/85"
        )}
      >
        {frame + 1}
      </span>
    </button>
  );
}

export default function FrameFilmstrip({
  getFrameThumbnail,
  totalFrames,
  currentFrame,
  selectedFrames,
  onCurrentFrameChange,
  onSelectionChange,
  onExtract,
  isPlaying,
  onTogglePlay,
  onStepFrame,
  onGoToFirst,
  fps,
  onFpsChange,
}) {
  const stripRef = useRef(null);
  const lastAnchorRef = useRef(0);
  const safeTotal = Math.max(1, totalFrames || 1);
  const selectedCount = selectedFrames?.size ?? 0;

  const scrollThumbIntoView = useCallback((frame) => {
    const strip = stripRef.current;
    if (!strip) return;
    const step = THUMB_W + THUMB_GAP;
    const left = frame * step;
    const right = left + THUMB_W;
    const pad = 16;
    if (left < strip.scrollLeft + pad) {
      strip.scrollLeft = Math.max(0, left - pad);
    } else if (right > strip.scrollLeft + strip.clientWidth - pad) {
      strip.scrollLeft = right - strip.clientWidth + pad;
    }
  }, []);

  useEffect(() => {
    scrollThumbIntoView(currentFrame);
  }, [currentFrame, scrollThumbIntoView]);

  const handleFrameClick = useCallback((frame) => {
    lastAnchorRef.current = frame;
    onCurrentFrameChange(frame);
  }, [onCurrentFrameChange]);

  const handleToggleSelect = useCallback((frame, range) => {
    const next = new Set(selectedFrames);
    if (range) {
      const anchor = lastAnchorRef.current;
      const lo = Math.min(anchor, frame);
      const hi = Math.max(anchor, frame);
      for (let i = lo; i <= hi; i++) next.add(i);
    } else if (next.has(frame)) {
      next.delete(frame);
    } else {
      next.add(frame);
    }
    lastAnchorRef.current = frame;
    onSelectionChange(next);
  }, [selectedFrames, onSelectionChange]);

  const handleSelectAll = () => {
    const all = new Set();
    for (let i = 0; i < safeTotal; i++) all.add(i);
    onSelectionChange(all);
  };

  const handleClearSelection = () => onSelectionChange(new Set());

  const handleSliderChange = ([v]) => onCurrentFrameChange(v);

  const shouldPreload = (i) => Math.abs(i - currentFrame) <= PRELOAD_RADIUS;

  const darkBtn =
    "h-8 w-full px-3 text-xs font-medium border bg-zinc-800 text-zinc-100 border-zinc-600 hover:bg-zinc-700 hover:text-white disabled:opacity-100 disabled:bg-zinc-800/70 disabled:text-zinc-400 disabled:border-zinc-600 disabled:hover:bg-zinc-800/70 disabled:hover:text-zinc-400";
  const darkBtnPrimary =
    "h-8 w-full px-3 text-xs font-medium gap-1.5 bg-primary text-white hover:bg-primary/90 disabled:opacity-100 disabled:bg-zinc-700 disabled:text-zinc-300 disabled:hover:bg-zinc-700 disabled:hover:text-zinc-300";

  if (!getFrameThumbnail) return null;

  return (
    <div
      className="bg-zinc-950 border-t border-zinc-800 flex flex-col flex-shrink-0 text-zinc-100"
      data-testid="frame-filmstrip"
    >
      <div className="px-3 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-2 border-b border-zinc-800/80 bg-zinc-900/50">
        <div className="flex items-center gap-2">
          <Film className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-semibold text-zinc-100">Frames</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 tabular-nums">
            {safeTotal}
          </span>
        </div>
        <p className="text-[10px] text-zinc-400">
          {selectedCount > 0
            ? `${selectedCount} selected for extract`
            : "Click frame to view · ⌘/Ctrl+click to select · Shift+click for range"}
        </p>
      </div>

      <div className="px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-2 sm:gap-3 border-b border-zinc-800/60">
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800"
            onClick={onGoToFirst}
            aria-label="First frame"
            data-testid="cine-first"
          >
            <SkipBack className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800"
            onClick={() => onStepFrame(false)}
            aria-label="Previous frame"
            data-testid="cine-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/15"
            onClick={onTogglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
            data-testid="cine-play"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800"
            onClick={() => onStepFrame(true)}
            aria-label="Next frame"
            data-testid="cine-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-[140px]">
          <span className="text-[11px] text-zinc-300 w-10 text-right tabular-nums font-medium">
            {currentFrame + 1}
          </span>
          <Slider
            min={0}
            max={safeTotal - 1}
            step={1}
            value={[currentFrame]}
            onValueChange={handleSliderChange}
            className="flex-1 [&_[role=slider]]:bg-primary"
            data-testid="frame-slider"
          />
          <span className="text-[11px] text-zinc-400 w-10 tabular-nums">{safeTotal}</span>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-zinc-400 hidden sm:inline">FPS</span>
          <select
            value={fps}
            onChange={(e) => onFpsChange(Number(e.target.value))}
            className="h-8 text-[11px] bg-zinc-800 border border-zinc-600 rounded-md px-2 text-zinc-100 focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="cine-fps"
          >
            {[5, 10, 15, 24, 30].map((v) => (
              <option key={v} value={v} className="bg-zinc-900">{v}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="px-3 sm:px-4 py-3 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div
          ref={stripRef}
          className="flex gap-2 overflow-x-auto flex-1 py-1 px-0.5 min-h-[92px] scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-zinc-900"
          data-testid="frame-thumbs-scroll"
        >
          {Array.from({ length: safeTotal }, (_, i) => (
            <FrameSlide
              key={i}
              frame={i}
              getFrameThumbnail={getFrameThumbnail}
              isActive={currentFrame === i}
              isSelected={selectedFrames?.has(i)}
              shouldLoad={shouldPreload(i)}
              onClick={handleFrameClick}
              onToggleSelect={handleToggleSelect}
            />
          ))}
        </div>

        <div className="flex flex-col gap-1.5 flex-shrink-0 w-full lg:w-36 xl:w-40">
          <Button
            variant="ghost"
            size="sm"
            className={darkBtn}
            onClick={handleSelectAll}
            data-testid="select-all-frames"
          >
            Select all
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={darkBtn}
            onClick={handleClearSelection}
            disabled={selectedCount === 0}
            data-testid="clear-frame-selection"
          >
            Clear
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={darkBtnPrimary}
            onClick={onExtract}
            disabled={selectedCount === 0}
            data-testid="extract-frames-btn"
          >
            <Download className="w-3.5 h-3.5" />
            Extract{selectedCount > 0 ? ` (${selectedCount})` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}
